import { GLOBAL_ROLE } from "./fileVisibility";

/**
 * Works out who should be in /systemManagers.
 *
 * /systemManagers is the database's own list of people who may read and write
 * every user's personal data. It is deliberately console-only: the rules give
 * it ".write": false, and that is the whole reason it can be trusted where
 * users/$uid/role cannot. A rule that trusted the role string would let anyone
 * who can write their own user record promote themselves.
 *
 * So this produces the JSON to paste, and nothing here writes anything.
 */

/**
 * One row per person who is either a System Digital Manager or already in the
 * list — so both directions of drift are visible, including someone left in
 * the list after losing the role.
 */
export function buildSystemManagerRows(users, systemManagers) {
    const managers = systemManagers || {};
    const records = users || {};

    const uids = new Set();

    Object.keys(records).forEach((uid) => {
        if (records[uid] && records[uid].role === GLOBAL_ROLE) {
            uids.add(uid);
        }
    });

    Object.keys(managers).forEach((uid) => {
        if (managers[uid] === true) {
            uids.add(uid);
        }
    });

    return Array.from(uids)
        .map((uid) => {
            const user = records[uid];
            const name = user
                ? [user.firstName, user.lastName].filter(Boolean).join(" ") ||
                  "(no name)"
                : "(no user record)";

            return {
                uid: uid,
                name: name,
                displayRole: user
                    ? user.customRoleName || user.role || "(no role)"
                    : "(no user record)",
                hasRole: !!user && user.role === GLOBAL_ROLE,
                inList: managers[uid] === true,
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
}

/** The node's contents, ready to paste at /systemManagers. */
export function buildSystemManagersJson(selectedUids) {
    const entries = Array.from(new Set(selectedUids))
        .sort()
        .map((uid) => [uid, true]);

    return JSON.stringify(Object.fromEntries(entries), null, 2);
}

export function summariseSystemManagers(rows, selectedUids) {
    const selected = new Set(selectedUids);

    return {
        selected: selected.size,
        // Holds the role but would not be in the list: loses the ability to
        // manage users outside their own admins map once the rules land.
        roleWithoutList: rows.filter(
            (row) => row.hasRole && !selected.has(row.uid),
        ).length,
        // In the list without the role: still granted everything, which is
        // worth seeing rather than discovering later.
        listWithoutRole: rows.filter(
            (row) => !row.hasRole && selected.has(row.uid),
        ).length,
        // What is actually live right now.
        live: rows.filter((row) => row.inList).length,
    };
}

/**
 * What a change of role means for /systemManagers.
 *
 * Returns "add", "remove" or null. Demotion matters as much as promotion:
 * leaving someone in the list after they stop being a System Digital Manager
 * keeps their access to every user's personal data.
 */
export function planSystemManagerChange(previousRole, nextRole) {
    const wasManager = previousRole === GLOBAL_ROLE;
    const isManager = nextRole === GLOBAL_ROLE;

    if (isManager && !wasManager) {
        return "add";
    }

    if (wasManager && !isManager) {
        return "remove";
    }

    return null;
}

/** True when the pasted list would differ from what is live. */
export function hasPendingChange(rows, selectedUids) {
    const selected = new Set(selectedUids);

    return rows.some((row) => row.inList !== selected.has(row.uid));
}
